import { useState, useEffect, useCallback } from "react";
import { db } from "../../firebaseClient";
import { computeDashboardSummary, computeTrueDashboardSummary, TodoItem } from "../services/todosSummary";
import type { DailySummaryResult } from "../services/dailySummaryService";
import { getTrueTodosOnce } from "../services/trueTodoAggregator";

// === Raw Todo 殘留資料排除（最小侵入） ===
// todos 是由 approvalFlows 推導出的索引；若 target 文件已刪除（常見於測試資料）但 flow/todo 沒清乾淨，
// 就會在 Live via Todos / Debug Raw Todos 看到一堆「幽靈待辦」。
// 這裡不動 Firestore，只用「target 是否仍存在」來排除顯示/統計。
type RawTodoDoc = TodoItem & { module?: string; moduleKey?: string; entityId?: string; entityNo?: string };

const MODULE_TARGET_COLLECTION: Record<string, string> = {
  CONTRACT_REVIEW: "approval_requests",
  SEAL_REQUEST: "approval_requests",
  OFFICIAL_REPLY: "approval_requests",
  TEMPLATE_STD: "contract_templates",
  GOV_PROJECT: "compliance_governance_projects_v2",
};

type TargetCheck = { exists: boolean; status?: string; isOpen?: boolean };
const _targetCheckCache = new Map<string, TargetCheck>();
let _pruneSeq = 0;

function _isApprovalRequestOpenStatus(status: string | undefined | null): boolean {
  const s = String(status || "").toLowerCase();
  if (!s) return true;
  // 只把「明確已結案/不需處理」排除；RETURNED/LEGAL_RETURNED 仍算待辦
  const closed = new Set([
    "approved",
    "rejected",
    "fully_approved",
    "completed",
    "cancelled",
    "canceled",
    "published",
    "draft",
    "not_started",
  ]);
  return !closed.has(s);
}

function _todoKey(t: RawTodoDoc): string {
  const mod = (t as any).module || (t as any).moduleKey || "UNKNOWN";
  const eid = (t as any).entityId || (t as any).id || "";
  return `${mod}:${eid}`;
}

async function pruneStaleTodos(todos: RawTodoDoc[]): Promise<RawTodoDoc[]> {
  const toCheck: RawTodoDoc[] = [];
  for (const t of todos) {
    const mod = (t as any).module || (t as any).moduleKey;
    const eid = (t as any).entityId;
    if (!mod || !eid) continue;
    const col = MODULE_TARGET_COLLECTION[mod];
    if (!col) continue;
    const k = _todoKey(t);
    if (!_targetCheckCache.has(k)) toCheck.push(t);
  }

  const CONCURRENCY = 12;
  let idx = 0;
  const worker = async () => {
    while (idx < toCheck.length) {
      const t = toCheck[idx++];
      const mod = (t as any).module || (t as any).moduleKey;
      const eid = (t as any).entityId;
      const col = MODULE_TARGET_COLLECTION[mod];
      const k = _todoKey(t);
      try {
        const snap = await db.collection(col).doc(eid).get();
        if (!snap.exists) {
          _targetCheckCache.set(k, { exists: false });
        } else {
          const data: any = snap.data() || {};
          const status = String(data?.status || "");
          const isOpen = col === "approval_requests" ? _isApprovalRequestOpenStatus(status) : true;
          // 若 target 已結案，視為「不應再被 raw todo 算進待辦」
          _targetCheckCache.set(k, { exists: true, status, isOpen });
        }
      } catch {
        // 讀不到就不要硬砍，避免誤傷
        _targetCheckCache.set(k, { exists: true, isOpen: true });
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, toCheck.length || 1) }, () => worker())
  );

  return todos.filter((t) => {
    const mod = (t as any).module || (t as any).moduleKey;
    const eid = (t as any).entityId;
    const col = mod ? MODULE_TARGET_COLLECTION[mod] : null;
    if (!col || !eid) return true;
    const k = _todoKey(t);
    const ck = _targetCheckCache.get(k);
    if (!ck) return true;
    if (ck.exists === false) return false;
    if (col === "approval_requests" && ck.isOpen === false) return false;
    return true;
  });
}

export type LiveStatus = "connecting" | "connected" | "offline" | "error" | "paused";

export interface LogEvent {
  time: string;
  type: "START" | "STOP" | "SNAPSHOT" | "PROBE" | "RETRY" | "COMPUTE" | "ERROR" | "NETWORK";
  payload?: string;
}

// --- Global Singleton State ---
interface GlobalState {
  liveStatus: LiveStatus;
  liveMsg: string;
  data: DailySummaryResult | null;
  lastSnapshotAt: number | null;
  lastSnapshotFromCache: boolean | null;
  snapshotCount: number;
  lastError: { code?: string; message?: string } | null;
  retryAttempt: number;
  retryNextAt: number | null;
  isListening: boolean;
  networkOnline: boolean;
  lastComputeAt: number | null;
  probe: {
    lastProbeAt: number | null;
    ok: boolean | null;
    errorCode?: string;
    errorMessage?: string;
  };
  eventLog: LogEvent[];
}

const INITIAL_STATE: GlobalState = {
  liveStatus: "connecting",
  liveMsg: "初始化中...",
  data: null,
  lastSnapshotAt: null,
  lastSnapshotFromCache: null,
  snapshotCount: 0,
  lastError: null,
  retryAttempt: 0,
  retryNextAt: null,
  isListening: false,
  networkOnline: true,
  lastComputeAt: null,
  probe: { lastProbeAt: null, ok: null },
  eventLog: [],
};

// Module-level variables (Singleton)
let currentState: GlobalState = { ...INITIAL_STATE };
let listeners: Array<(s: GlobalState) => void> = [];
let unsubTodosRef: (() => void) | null = null;
let unsubFlowsRef: (() => void) | null = null;
let unsubRequestsRef: (() => void) | null = null;
let retryTimer: any = null;
let heartbeatTimer: any = null;
let recomputeTimer: any = null;
let pendingReasons = new Set<string>();
let lastRawTodos: RawTodoDoc[] = [];

// --- Internal Helper: State Updates ---
function updateState(updates: Partial<GlobalState>) {
  currentState = { ...currentState, ...updates };
  listeners.forEach((listener) => listener(currentState));
}

function addLog(type: LogEvent["type"], payloadObj?: any) {
  const entry: LogEvent = {
    time: new Date().toLocaleTimeString(),
    type,
    payload: payloadObj
      ? typeof payloadObj === "string"
        ? payloadObj
        : JSON.stringify(payloadObj)
      : undefined,
  };
  const newLog = [entry, ...currentState.eventLog].slice(0, 200);
  updateState({ eventLog: newLog });
  console.log(`[TodosLiveMonitor] ${type}`, payloadObj || "");
}

// --- Internal Helper: Probe ---
async function performProbe(silent = false) {
  if (!silent) addLog("PROBE", "Starting server probe...");
  const start = Date.now();
  try {
    await db.collection("todos").limit(1).get({ source: "server" });
    updateState({
      probe: { lastProbeAt: Date.now(), ok: true },
    });
    if (!silent) addLog("PROBE", `Success (${Date.now() - start}ms)`);

    if (currentState.liveStatus === "error" || currentState.liveStatus === "offline") {
      updateState({ liveStatus: "connected", liveMsg: "連線正常 (Probe Recovered)" });
    }
  } catch (err: any) {
    updateState({
      probe: {
        lastProbeAt: Date.now(),
        ok: false,
        errorCode: err.code,
        errorMessage: err.message,
      },
      lastError: { code: err.code, message: err.message },
    });
    if (!silent) addLog("ERROR", `Probe Failed: ${err.code}`);
  }
}

// --- Internal Helper: Cleanup ---
function cleanup() {
  try {
    unsubTodosRef?.();
  } catch {}
  try {
    unsubFlowsRef?.();
  } catch {}
  try {
    unsubRequestsRef?.();
  } catch {}
  unsubTodosRef = null;
  unsubFlowsRef = null;
  unsubRequestsRef = null;

  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  if (heartbeatTimer) clearInterval(heartbeatTimer);
  heartbeatTimer = null;
  if (recomputeTimer) clearTimeout(recomputeTimer);
  recomputeTimer = null;

  pendingReasons = new Set<string>();
}


// --- Internal Helper: Retry Scheduling ---
function scheduleRetry() {
  const attempt = (currentState.retryAttempt || 0) + 1;
  const delayMs = Math.min(30000, 1000 * Math.pow(2, Math.min(attempt, 5)));
  const nextAt = Date.now() + delayMs;

  updateState({
    retryAttempt: attempt,
    retryNextAt: nextAt,
    liveMsg: `監控連線中斷，${Math.round(delayMs / 1000)} 秒後重試...`,
  });

  addLog("RETRY", { attempt, delayMs });

  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = setTimeout(() => {
    manualReconnect();
  }, delayMs);
}

// --- Internal Helper: Start Listener ---
function startListener() {
  if (unsubTodosRef || unsubFlowsRef || unsubRequestsRef) return;

  updateState({ isListening: true, liveStatus: "connecting", liveMsg: "建立監聽中..." });
  addLog("START", "Starting listeners: todos + approvalFlows + approval_requests");

  const scheduleRecompute = (reason: string) => {
    pendingReasons.add(reason);
    if (recomputeTimer) clearTimeout(recomputeTimer);
    recomputeTimer = setTimeout(async () => {
      const reasons = Array.from(pendingReasons.values());
      pendingReasons = new Set<string>();
      recomputeTimer = null;

      try {

        // True todos 由 approvals + raw todos 聚合而來。
        // 但 raw todos 只用來「觀測 / debug」，絕不能直接進入 Dashboard 與待辦清單，
        // 否則會出現 UUID/舊資料造成的「幽靈待辦」。
        const { trueTodos } = await getTrueTodosOnce({ limit: 500 });

        const rawTrue = trueTodos.filter((t) => t.source === "RAW_TODO");
        const nonRawTrue = trueTodos.filter((t) => t.source !== "RAW_TODO");

        // raw todos 仍做清理後留作 debug（不會進入 UI 計數/清單）
        const prunedRawTrue = await pruneStaleTodos(rawTrue);

        // ✅ UI 顯示只採用 non-raw true todos
        const merged = nonRawTrue;

        const summary = computeTrueDashboardSummary(merged);

        updateState({
          data: summary,
          lastComputeAt: Date.now(),
          liveMsg: `已同步（${merged.length} 筆 true todo；raw 偵測 ${prunedRawTrue.length} 筆但不計入；觸發：${reasons.join(", ")}）`,
          liveStatus: (typeof navigator !== "undefined" ? navigator.onLine : true) ? "connected" : "offline",
        });
        addLog("COMPUTE", `Computed summary from trueTodos=${merged.length} (reasons: ${reasons.join(",")})`);
      } catch (err: any) {
        // ❌ 禁止 fallback 到 raw todos，否則幽靈待辦會回來。
        // 保留既有畫面（上一版成功聚合的結果），只把狀態標為 error 並記錄原因。
        updateState({
          liveStatus: "error",
          liveMsg: `True Todo 聚合失敗（已維持上一版結果）：${err?.message || String(err)}`,
        });
        addLog("ERROR", `Compute failed; keep last good summary. ${err?.message || String(err)}`);
      }
    }, 250);
  };

  // 1) Raw todos listener
  const todosQuery = db
    .collection("todos")
    .where("status", "==", "OPEN")
    .orderBy("createdAt", "desc")
    .limit(500);

  unsubTodosRef = todosQuery.onSnapshot(
    { includeMetadataChanges: true },
    (snap: any) => {
      const fromCache = !!snap.metadata?.fromCache;
      const size = snap.size ?? 0;

      const rows: RawTodoDoc[] = [];
      snap.forEach((doc: any) => {
        rows.push({ id: doc.id, ...(doc.data() as any) });
      });
      lastRawTodos = rows;

      updateState({
        lastSnapshotAt: Date.now(),
        lastSnapshotFromCache: fromCache,
        networkOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
        liveStatus: fromCache ? "connecting" : "connected",
        liveMsg: fromCache
          ? `同步中（cache；raw 偵測 ${size} 筆，不計入 Dashboard/待辦）`
          : `連線正常（raw 偵測 ${size} 筆，不計入 Dashboard/待辦）`,
      });
      addLog("SNAPSHOT", `todos snapshot size=${size} cache=${fromCache}`);

      scheduleRecompute("RAW_TODOS");
    },
    (err: any) => {
      updateState({ liveStatus: "error", liveMsg: `raw todos 監聽錯誤：${err?.message || String(err)}` });
      addLog("ERROR", `todos onSnapshot error: ${err?.message || String(err)}`);
      scheduleRetry();
    }
  );

  // 2) approvalFlows listener
  unsubFlowsRef = db.collection("approvalFlows").onSnapshot(
    { includeMetadataChanges: true },
    (snap: any) => {
      const fromCache = !!snap.metadata?.fromCache;
      updateState({
        lastSnapshotAt: Date.now(),
        lastSnapshotFromCache: fromCache,
        liveStatus: fromCache ? "connecting" : "connected",
      });
      addLog("SNAPSHOT", `approvalFlows snapshot cache=${fromCache} size=${snap.size ?? "?"}`);
      scheduleRecompute("APPROVAL_FLOWS");
    },
    (err: any) => {
      updateState({ liveStatus: "error", liveMsg: `approvalFlows 監聽錯誤：${err?.message || String(err)}` });
      addLog("ERROR", `approvalFlows onSnapshot error: ${err?.message || String(err)}`);
      scheduleRetry();
    }
  );

  // 3) approval_requests listener
  unsubRequestsRef = db.collection("approval_requests").onSnapshot(
    { includeMetadataChanges: true },
    (snap: any) => {
      const fromCache = !!snap.metadata?.fromCache;
      updateState({
        lastSnapshotAt: Date.now(),
        lastSnapshotFromCache: fromCache,
        liveStatus: fromCache ? "connecting" : "connected",
      });
      addLog("SNAPSHOT", `approval_requests snapshot cache=${fromCache} size=${snap.size ?? "?"}`);
      scheduleRecompute("APPROVAL_REQUESTS");
    },
    (err: any) => {
      updateState({ liveStatus: "error", liveMsg: `approval_requests 監聽錯誤：${err?.message || String(err)}` });
      addLog("ERROR", `approval_requests onSnapshot error: ${err?.message || String(err)}`);
      scheduleRetry();
    }
  );

  // Heartbeat: re-probe every minute
  heartbeatTimer = setInterval(() => {
    void performProbe(false);
  }, 60_000);

  // Initial compute soon (even before first snapshot)
  scheduleRecompute("INIT");
}


// --- Internal Helper: Reconnect ---
async function manualReconnect() {
  addLog("NETWORK", "Manual Reconnect Triggered");
  cleanup();
  updateState({
    liveStatus: "connecting",
    liveMsg: "正在重置網路並重新連線...",
    retryAttempt: 0,
    lastError: null,
  });

  try {
    await db.disableNetwork();
    await new Promise((r) => setTimeout(r, 500));
    await db.enableNetwork();
    addLog("NETWORK", "Network cycled");

    await performProbe(false);
    startListener();
  } catch (e: any) {
    updateState({ liveStatus: "error", liveMsg: "重連失敗" });
    addLog("ERROR", e?.message || "Reconnect failed");
    scheduleRetry();
  }
}

// --- Public Hook ---
export function useTodosLiveMonitor(enabled = true) {
  const [state, setState] = useState<GlobalState>(currentState);

  useEffect(() => {
    if (!enabled) return;

    const onStateChange = (s: GlobalState) => setState({ ...s });
    listeners.push(onStateChange);

    // Auto start once
    startListener();

    const onOnline = () => {
      updateState({ networkOnline: true });
      addLog("NETWORK", "Browser online");
      manualReconnect();
    };
    const onOffline = () => {
      updateState({ networkOnline: false, liveStatus: "offline", liveMsg: "網路離線..." });
      addLog("NETWORK", "Browser offline");
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    return () => {
      listeners = listeners.filter((l) => l !== onStateChange);
      if (listeners.length === 0) cleanup();
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [enabled]);

  const start = useCallback(() => startListener(), []);
  const stop = useCallback(() => {
    cleanup();
    updateState({ isListening: false, liveStatus: "paused", liveMsg: "監聽已停止" });
    addLog("STOP", "Listener stopped");
  }, []);

  const reconnect = useCallback(() => manualReconnect(), []);
  const probe = useCallback(() => performProbe(false), []);

  return {
    ...state,
    startListener: start,
    stopListener: stop,
    reconnect,
    probe,
  };
}
